<template>
  <div class="flex flex-col pb-12">
    <div class="flex items-center gap-2 mb-6">
      <Button
        icon="pi pi-angle-left"
        size="small"
        rounded
        aria-label="arrow right"
        @click="router.back()"
      />
    </div>
    <h1
      class="text-2xl min-[1300px]:text-5xl text-lumar-blue font-bold mb-2 min-[1300px]:mb-[96px]"
    >
      Barvna paleta hiše
    </h1>
    <span class="min-[1300px]:hidden text-sm text-lumar-dark-gray mb-6">{{ houseModel }}</span>
    <div v-if="showError" class="flex flex-col">
      <span class="text-lumar-gray"
        >Trenutno ne moremo prikazati barvne palete! Prosimo poskusite pozneje.</span
      >
    </div>
    <div v-if="!showError">
      <div
        v-for="(colorGroup, area) in colorGroupsWithValues"
        :key="area"
        class="flex flex-col border-b border-lumar-medium-gray mb-6"
      >
        <h2 class="text-xl text-lumar-blue mb-4">
          {{ $t(area as string) }}
        </h2>
        <div
          class="flex flex-col min-[1300px]:grid min-[1300px]:grid-cols-4 gap-1.5 min-[1300px]:gap-2.5 mb-6"
        >
          <ColorCard
            v-for="(color, areaPart) in colorGroup"
            :key="areaPart"
            :color="color.title"
            :ral="color.ral"
            :area-part="areaPart.toString()"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ColorCard from '@/components/ColorCard.vue';
import type { components } from '@/lib/api/v1';
import { useAppApi } from '@/plugins/appApi';
import { useProjectStore } from '@/stores/project';
import { onMounted, reactive, ref, watch, type Ref } from 'vue';
import { useRouter } from 'vue-router';

interface Color {
  title: string;
  ral: string;
}

const showError = ref<boolean>(false);
const router = useRouter();
const colors: Ref<components['schemas']['ColorDto'][]> = ref([]);
const colorGroupsWithValues: Ref<{
  [houseArea: string]: { [areaPart: string]: Color };
}> = ref({});

const projectStore = useProjectStore();
const houseModel = ref('');

const colorGroups = reactive<{
  [houseArea: string]: { [areaPart: string]: Color };
}>({
  'colors.roof': {
    'colors.roof.cover': { title: '', ral: '' },
    'colors.roof.supportColumns': { title: '', ral: '' }
  },
  'colors.facade': {
    'colors.facade.finish1': { title: '', ral: '' },
    'colors.facade.finish2': { title: '', ral: '' },
    'colors.facade.finish3': { title: '', ral: '' }
  },
  'colors.windowsAndDoors': {
    'colors.windowsAndDoors.windowsOutside': { title: '', ral: '' },
    'colors.windowsAndDoors.windowsInside': { title: '', ral: '' },
    'colors.windowsAndDoors.blinders.bars': { title: '', ral: '' },
    'colors.windowsAndDoors.blinders.guides': { title: '', ral: '' },
    'colors.windowsAndDoors.frontDoor.outside': { title: '', ral: '' },
    'colors.windowsAndDoors.frontDoor.inside': { title: '', ral: '' },
    'colors.windowsAndDoors.innerDoors': { title: '', ral: '' }
  },
  'colors.floors': {
    'colors.floors.parquet.type': { title: '', ral: '' }
  }
});

onMounted(async () => {
  const client = useAppApi();
  try {
    const response = await client.GET('/api/projects/{uid}/colors', {
      params: {
        path: {
          uid: projectStore.getProjectUid()
        }
      }
    });

    colors.value = response.data!;

    const infoResponse = await client.GET('/api/projects/{uid}', {
      params: {
        path: {
          uid: projectStore.getProjectUid()
        }
      }
    });
    houseModel.value = infoResponse.data!.houseModel!;
  } catch (e) {
    showError.value = true;
    console.error(e);
  }
});

watch(colors, () => {
  // Map the set server-side colors to the color matrix.
  mapServerColorsToColorMatrix();

  // Filter out the colors with no value set, we don't want to display those.
  setColorGroupsWithValues();
});

// eslint-disable-next-line complexity
function setColorGroupsWithValues() {
  colorGroupsWithValues.value = {};

  for (const area in colorGroups) {
    const parts = colorGroups[area];

    for (const part in parts) {
      if (!parts[part].ral) {
        continue;
      }

      if (!colorGroupsWithValues.value[area]) {
        colorGroupsWithValues.value[area] = {};
      }

      colorGroupsWithValues.value[area][part] = parts[part];
    }
  }
}

function mapServerColorsToColorMatrix() {
  for (let i = 0; i < colors.value.length; i++) {
    const color = colors.value[i];
    if (colorGroups[color.houseArea!] && colorGroups[color.houseArea!][color.areaPart!]) {
      colorGroups[color.houseArea!][color.areaPart!].ral = color.ral!;
      colorGroups[color.houseArea!][color.areaPart!].title = color.title!;
    }
  }
}
</script>
